import { useState, useContext, useEffect } from "react";
import { DateTime } from "luxon";
import Headline from "../Components/Elements/Headline";
import StartMessage from "../Components/Elements/StartMessage";
import { AuthContext, YearContext } from "../context/context";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import CalendarComponent from "../Components/Cards/CardCalendar";
import MobileCalendar from "../Components/Cards/MobileCalendar";
import CardAddNote from "../Components/Cards/CardAddNote";
import { submitNote, deleteNote } from "../api";
import { useLanguage } from "../context/LanguageContext";
import CardMilestones from "../Components/Cards/CardMilestones";
import dayjs from "dayjs";

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const Milestones = () => {
  const today = DateTime.local();
  const [activeDay, setActiveDay] = useState(today);
  const { userData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);
  const [milestones, setMilestones] = useState([]);
  const [triggerNotePopup, setTriggerNotePopup] = useState(false);
  const [triggerViewNote, setTriggerViewNote] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for Tasks, 1 for Journal
  const { copy } = useLanguage();
  const isMobile = useIsMobile();

  // Add loading state
  if (!userData) {
    return (
      <PageContentContainer>
        <div className="flex items-center justify-center h-full">
          <p>{copy.milestones.loading}</p>
        </div>
      </PageContentContainer>
    );
  }

  const handleAddNote = async (title, description, note_date) => {
    try {
      await submitNote(
        title,
        description,
        note_date,
        userData.id,
        selectedYear
      );
    } catch (err) {
      console.error("Failed to submit note:", err.message);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
    } catch (err) {
      console.error("Error deleting note:", err.message);
    }
  };

  // Handle adding a new note for a specific day
  const handleAddNoteForDay = (day) => {
    setActiveDay(day);
    setTriggerNotePopup(true);
    // Reset the trigger after a short delay
    setTimeout(() => setTriggerNotePopup(false), 100);
  };

  // Handle viewing an existing note for a specific day
  const handleViewNoteForDay = (day) => {
    setActiveDay(day);
    setTriggerViewNote(true);
    // Reset the trigger after a short delay
    setTimeout(() => setTriggerViewNote(false), 100);
  };

  const filteredNotes = (userData?.notes || []).filter((note) => {
    return (
      dayjs(note.note_date).format("YYYY-MM-DD") ===
      dayjs(activeDay).format("YYYY-MM-DD")
    );
  });

  const goals = Array.isArray(userData?.goals) ? userData.goals : [];

  return (
    <PageContentContainer>
      {goals.length > 0 ? (
        <>
          <div className="hidden md:block">
            <Headline
              headline={copy.milestones.headline}
              description={copy.milestones.description}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[5fr_2fr] w-full gap-1 md:gap-2 h-auto md:h-full">
            {isMobile ? (
              <MobileCalendar
                activeDay={activeDay}
                setActiveDay={setActiveDay}
                userData={userData}
                onAddNote={handleAddNoteForDay}
                onViewNote={handleViewNoteForDay}
              />
            ) : (
              <CalendarComponent
                activeDay={activeDay}
                setActiveDay={setActiveDay}
                userData={userData}
              />
            )}

            {isMobile ? (
              <div className="w-full overflow-hidden mobile-container">
                <div className="card-base card-base-background card-base-border p-3 min-h-full mb-10 w-full">
                  {/* Toggle Tabs */}
                  <div className="flex justify-center !mb-2">
                    <div className="flex flex-row card-base-border rounded-md w-fit">
                    <div
                      className={`py-2 px-4 cursor-pointer text-sm ${activeTab === 0 ? "bg-accent-light " : " hover:bg-gray-100 "} `}
                      onClick={() => setActiveTab(0)}
                    >
                      Milestones
                    </div>
                    <div
                      className={`py-2 px-4 cursor-pointer text-sm ${activeTab === 1 ? "bg-accent-light " : " hover:bg-gray-100 "} `}
                      onClick={() => setActiveTab(1)}
                    >
                      Journal
                    </div>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="h-full w-full overflow-hidden">
                    {activeTab === 0 && (
                      <div className="h-full w-full overflow-hidden">
                        <CardMilestones
                          milestones={milestones}
                          setMilestones={setMilestones}
                          activeDay={activeDay}
                          setActiveDay={setActiveDay}
                          userData={userData}
                          className="h-full max-h-full w-full overflow-auto"
                        />
                      </div>
                    )}

                    {activeTab === 1 && (
                      <div className="h-full w-full overflow-hidden">
                        <CardAddNote
                          notes={filteredNotes}
                          activeDay={activeDay}
                          onSubmitNote={handleAddNote}
                          onDeleteNote={handleDeleteNote}
                          descriptionClassName="max-h-[60px] overflow-hidden line-clamp-3"
                          onExternalTrigger={triggerNotePopup}
                          onExternalViewTrigger={triggerViewNote}
                        />
                      </div>
                    )}
                  </div>

                  {/* Hidden CardAddNote for external triggers */}
                  <div className="hidden">
                    <CardAddNote
                      notes={filteredNotes}
                      activeDay={activeDay}
                      onSubmitNote={handleAddNote}
                      onDeleteNote={handleDeleteNote}
                      descriptionClassName="max-h-[60px] overflow-hidden line-clamp-3"
                      onExternalTrigger={triggerNotePopup}
                      onExternalViewTrigger={triggerViewNote}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4 md:gap-2 md:ml-2 h-full">
                {/* Milestones */}
                <div className="overflow-hidden md:basis-3/5 md:h-[60%]">
                  <CardMilestones
                    milestones={milestones}
                    setMilestones={setMilestones}
                    activeDay={activeDay}
                    setActiveDay={setActiveDay}
                    userData={userData}
                    className="h-full max-h-full pr-1 overflow-auto"
                    cardClassName="h-full"
                  />
                </div>

                {/* Add Note */}
                <div className="overflow-hidden md:basis-2/5 md:h-[40%]">
                  <CardAddNote
                    notes={filteredNotes}
                    activeDay={activeDay}
                    onSubmitNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                    descriptionClassName="max-h-[60px] overflow-hidden line-clamp-3"
                    cardClassName="h-full"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <StartMessage
          title={copy.start_message.headline}
          message={copy.start_message.description}
        />
      )}
    </PageContentContainer>
  );
};

export default Milestones;
